import { useRef, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";

export const PageHeader = ({ title, desc, children }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
    <div>
      <h1 className="font-body text-2xl font-bold text-gray-900">{title}</h1>
      {desc && <p className="mt-1 text-sm text-gray-500">{desc}</p>}
    </div>
    <div className="flex gap-2">{children}</div>
  </div>
);

export const Card = ({ title, children, className = "" }) => (
  <section className={`rounded-md border border-gray-200 bg-white p-5 ${className}`}>
    {title && <h2 className="mb-4 font-body text-base font-bold text-gray-900">{title}</h2>}
    {children}
  </section>
);

export const F = ({ label, value, onChange, textarea, testid, type = "text", dir, className = "", ...rest }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs text-gray-600">{label}</Label>
    {textarea ? (
      <Textarea data-testid={testid} dir={dir} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="min-h-[90px] bg-white" {...rest} />
    ) : (
      <Input data-testid={testid} type={type} dir={dir} value={value ?? ""} onChange={(e) => onChange(type === "number" ? e.target.value : e.target.value)} className="bg-white" {...rest} />
    )}
  </div>
);

export const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center justify-between gap-3 rounded border border-gray-200 px-3 py-2.5 text-sm">
    <span>{label}</span>
    <Switch data-testid={testid} checked={!!checked} onCheckedChange={onChange} />
  </label>
);

export const Btn = ({ children, variant = "dark", className = "", ...p }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 rounded px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
      variant === "dark" ? "bg-ink text-white hover:bg-gray-800" : variant === "danger" ? "border border-red-200 text-red-600 hover:bg-red-50" : "border border-gray-300 bg-white hover:bg-gray-50"
    } ${className}`}
    {...p}
  >
    {children}
  </button>
);

export const Uploader = ({ onUploaded, multiple = true, testid = "image-upload-input", label = "رفع صور" }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      const { data } = await api.post("/admin/upload", fd);
      onUploaded(data.urls);
      toast.success("تم رفع الصور");
    } catch (err) {
      toast.error(errText(err, "فشل رفع الصور"));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/*" multiple={multiple} hidden onChange={onPick} data-testid={testid} />
      <Btn variant="light" type="button" onClick={() => ref.current.click()} disabled={busy}>
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {label}
      </Btn>
    </>
  );
};

export const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));
